import { useRef, useState } from 'react';

import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  VStack,
  Box,
  HStack,
  Text,
  Button,
  Input,
} from '@chakra-ui/react';

import { ArrowUpIcon } from '@chakra-ui/icons';

interface WetSignUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpload: (file: File) => void;
  isLoading: boolean;
}

const WetSignUploadModal = ({
  isOpen,
  onClose,
  onUpload,
  isLoading,
}: WetSignUploadModalProps) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleSelectFile = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleUpload = () => {
    if (selectedFile) {
      onUpload(selectedFile);
      setSelectedFile(null);
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setIsDragging(false);
    onClose();
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      setSelectedFile(e.dataTransfer.files[0]);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Upload WET Signed Binder</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text mb="4" fontSize="sm" color="gray.600">
            Please select the WET signed binder document to upload:
          </Text>

          <Input
            type="file"
            accept=".pdf"
            ref={fileInputRef}
            onChange={handleFileChange}
            display="none"
          />

          <Box
            border="2px dashed"
            borderColor={isDragging ? 'brand.400' : 'gray.300'}
            borderRadius="md"
            p={6}
            bg={isDragging ? 'brand.50' : 'gray.50'}
            transition="all 0.2s"
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            textAlign="center"
            mb={4}
            cursor="pointer"
            _hover={{ borderColor: 'brand.400', bg: 'brand.50' }}
          >
            <VStack spacing={3}>
              <ArrowUpIcon
                boxSize={10}
                color={isDragging ? 'brand.500' : 'gray.500'}
              />
              <Text fontWeight="medium">
                {isDragging
                  ? 'Drop your PDF file here'
                  : 'Drag and drop your PDF file here, or click to select'}
              </Text>
              <Button
                size="sm"
                onClick={handleSelectFile}
                colorScheme="brand"
                variant="outline"
                disabled={isLoading}
              >
                Select PDF File
              </Button>
            </VStack>
          </Box>

          {selectedFile && (
            <Box
              mt={4}
              p={3}
              borderRadius="md"
              bg="green.50"
              border="1px solid"
              borderColor="green.200"
            >
              <HStack justifyContent="space-between">
                <Text fontWeight="medium" noOfLines={1} color="green.700">
                  {selectedFile.name}
                </Text>
                <Text fontSize="sm" color="green.600">
                  {(selectedFile.size / 1024).toFixed(0)} KB
                </Text>
              </HStack>
            </Box>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            variant="ghost"
            mr="3"
            onClick={handleClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            variant="brand"
            onClick={handleUpload}
            isLoading={isLoading}
            disabled={!selectedFile || isLoading}
          >
            Upload
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default WetSignUploadModal;
